import React, { useCallback } from "react";
import z from "zod";

import { Button, Divider, Form, Input } from "@skydio/rivet-ui";

import { useCreateAxonIdentityProvider } from "hooks/mutation/createAxonIdentityProvider";
import { displayMutationResults } from "utils/mutations";

import { SsoHelpLink } from "./SsoHelpLink";

export interface AxonCreateResults {
  axonMessage: string;
}

interface NewAxonConnectProviderPaneProps {
  organizationNodeId: string;
  connection?: string;
  onCancel?: () => void;
  onNext?: () => void;
}

const NewAxonConnectProviderPane = ({
  organizationNodeId,
  connection,
  onCancel,
  onNext,
}: NewAxonConnectProviderPaneProps) => {
  const newAxonSchema = z.object({
    agencyDomain: z
      .string()
      .min(1, "Agency Domain required")
      .regex(/^[a-zA-Z0-9\._-]+(evidence\.com|axon\.com)$/, {
        message: "Must be valid agency domain",
      }),
  });

  const [createAxonProvider, loading] = useCreateAxonIdentityProvider(
    displayMutationResults(response => response.createAxonIdentityProvider, {
      onSuccess: () => {
        onNext?.();
      },
      successMessage: () => "Axon Connect provider created successfully",
    })
  );

  const handleSubmit = useCallback(
    (values: z.infer<typeof newAxonSchema>) => {
      createAxonProvider(organizationNodeId, values.agencyDomain, [connection ?? ""]);
    },
    [createAxonProvider, organizationNodeId, connection]
  );

  return (
    <>
      <Divider />
      <Form
        schema={newAxonSchema}
        onSubmit={handleSubmit}
        className="flex flex-col items-start gap-2"
      >
        <div className="flex w-full flex-col gap-4">
          <Input
            label={
              <>
                Agency Domain <span className="text-red-500">*</span>
              </>
            }
            name="agencyDomain"
            placeholder="agency.region.evidence.com"
            isClearable
            isDisabled={loading}
            description="The domain of the agency that you are connecting to."
          />
        </div>
        <span className="flex w-full justify-between">
          <SsoHelpLink />
          <div className="flex gap-4 self-end">
            <Button onClick={onCancel}>Cancel</Button>
            <Button isLoading={loading} id="createProvider" variant="primary" type="submit">
              Create
            </Button>
          </div>
        </span>
      </Form>
    </>
  );
};

export default NewAxonConnectProviderPane;
